import React, { useMemo, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, BarChart, Bar, Cell, Legend,
} from 'recharts';
import { motion } from 'motion/react';
import { getUnsoldRows, getIncludedSold, getTotalProfit, getLivePrice } from './helpers';
import {
  ZONE, getItemTypes, findSkinImage, fmt, signed, pct, pnlColor,
  ZoneHead, ZoneFooter,
} from './zoneShared';

// Analytics Zone — Analytics 2 (the plain-English report) rebuilt on the
// Overview 7 board: same numbers, same explanations, hard-edged charts.
const TICK = { fill: '#131313', fontSize: 14.5, fontWeight: 800, fontFamily: 'Archivo, sans-serif' };
const GRID = 'rgba(19,19,19,0.14)';
const AXIS = '#131313';

const RANGES = [
  { key: 'all', label: 'All time', days: null },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '7d',  label: 'Last 7 days', days: 7 },
];

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="zn-tip">
      <div className="k">{label}</div>
      <div className="v" style={{ color: pnlColor(v) }}>{signed(v)}</div>
    </div>
  );
};

const PairTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="zn-tip">
      <div className="k">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="v" style={{ fontSize: 15.5, display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 11, height: 11, background: p.color, border: `2px solid ${ZONE.black}`, flexShrink: 0 }} />
          {p.name}: ${fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

function Info({ text }) {
  return <span className="zn-info" title={text} aria-label={text}>i</span>;
}

function Section({ num, title, scope, explain, children }) {
  return (
    <motion.section className="zn-section"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="zn-section-head">
        <span className="zn-section-num">{num}</span>
        <h2>{title}</h2>
        <span className="zn-section-scope">{scope}</span>
      </div>
      <p className="zn-explain">{explain}</p>
      {children}
    </motion.section>
  );
}

export default function AnalyticsTabZone({ rows, settings, livePrices, skinsData = [] }) {
  const [rangeKey, setRangeKey] = useState('all');
  const range = RANGES.find(r => r.key === rangeKey);

  const isExcluded = (r) => {
    const types = getItemTypes(r);
    const t = types.length ? settings?.itemTypes?.find(it => it.value === types[0].value) : null;
    return t?.overrideProfitLoss === true;
  };

  // Sold rows inside the selected range (range applies to *sales*).
  const rangeSold = useMemo(() => {
    let sold = getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value);
    if (range.days) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - range.days);
      sold = sold.filter(r => new Date(r.timestamp) >= cutoff);
    }
    return sold;
  }, [rows, range]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Headline figures ───────────────────────────────────────
  const figures = useMemo(() => {
    const profit    = rangeSold.reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);
    const revenue   = rangeSold.reduce((s, r) => s + (parseFloat(r.sellPrice) || 0), 0);
    const soldCost  = rangeSold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const wins      = rangeSold.filter(r => (parseFloat(r.profit) || 0) > 0).length;
    const winRate   = rangeSold.length ? Math.round(wins / rangeSold.length * 100) : null;
    const avgProfit = rangeSold.length ? profit / rangeSold.length : null;
    const roi       = soldCost > 0 ? profit / soldCost * 100 : null;

    // Current holdings — always "right now", independent of range.
    const unsold = getUnsoldRows(rows || []);
    const holdCost = unsold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    let holdValue = 0, priced = 0;
    unsold.forEach(r => {
      const p = getLivePrice(livePrices, r)?.starting_at?.price;
      if (p != null) { holdValue += p; priced++; }
      else holdValue += parseFloat(r.buyPrice) || 0;
    });
    const totalInvested = (rows || []).filter(r => r.skin?.value)
      .reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);

    return {
      profit, revenue, soldCost, winRate, wins, avgProfit, roi,
      soldCount: rangeSold.length,
      lifetimeProfit: getTotalProfit(rows || [], isExcluded),
      unsoldCount: unsold.length, holdCost, holdValue,
      holdPnl: holdValue - holdCost, priced, totalInvested,
    };
  }, [rangeSold, rows, livePrices]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cumulative profit (all time) ───────────────────────────
  const cumulative = useMemo(() => {
    const sold = getIncludedSold(rows || [], isExcluded)
      .filter(r => r.skin?.value)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let cum = 0;
    return sold.map(r => {
      cum += parseFloat(r.profit) || 0;
      return {
        date: new Date(r.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        profit: parseFloat(cum.toFixed(2)),
      };
    });
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Monthly P&L (all time) ─────────────────────────────────
  const monthly = useMemo(() => {
    const byMonth = {};
    getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value).forEach(r => {
      const d = new Date(r.timestamp);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = {
        key, label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), profit: 0, count: 0,
      };
      byMonth[key].profit += parseFloat(r.profit) || 0;
      byMonth[key].count  += 1;
    });
    return Object.values(byMonth)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(m => ({ ...m, profit: parseFloat(m.profit.toFixed(2)) }));
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const bestMonth  = useMemo(() => monthly.reduce((b, m) => !b || m.profit > b.profit ? m : b, null), [monthly]);
  const worstMonth = useMemo(() => monthly.reduce((w, m) => !w || m.profit < w.profit ? m : w, null), [monthly]);

  // ── Holdings value vs cost over time ───────────────────────
  const holdingsHistory = useMemo(() => {
    // Same definition of "held" as every other total on the page — a
    // traded-away row has an empty sellPrice forever and is not a holding.
    const unsold = getUnsoldRows(rows || []);
    if (!unsold.length) return [];
    const today = new Date().toISOString().slice(0, 10);
    const dateSet = new Set([today]);
    unsold.forEach(r => {
      if (r.timestamp) dateSet.add(new Date(r.timestamp).toISOString().slice(0, 10));
      (r.priceHistory || []).forEach(p => dateSet.add(p.date));
    });
    const dates = [...dateSet].sort();
    const getPrice = (row, date) => {
      const buyDate = row.timestamp ? new Date(row.timestamp).toISOString().slice(0, 10) : null;
      if (buyDate && date < buyDate) return null;
      if (date === today) {
        const live = getLivePrice(livePrices, row)?.starting_at?.price;
        if (live != null) return live;
      }
      const snaps = (row.priceHistory || []).filter(p => p.date <= date);
      if (snaps.length) {
        const latest = snaps[snaps.length - 1];
        return latest.buffPrice ?? latest.price ?? null;
      }
      if (buyDate && date >= buyDate) return parseFloat(row.buyPrice) || null;
      return null;
    };
    return dates.map(date => {
      let value = 0, cost = 0, counted = 0;
      unsold.forEach(r => {
        const p = getPrice(r, date);
        if (p == null) return;
        value += p;
        cost  += parseFloat(r.buyPrice) || 0;
        counted++;
      });
      return counted ? {
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(value.toFixed(2)),
        cost:  parseFloat(cost.toFixed(2)),
      } : null;
    }).filter(Boolean);
  }, [rows, livePrices]);

  // ── Winners / losers (in range) ────────────────────────────
  const winners = useMemo(() =>
    [...rangeSold].filter(r => (parseFloat(r.profit) || 0) > 0)
      .sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit)).slice(0, 5), [rangeSold]);
  const losers = useMemo(() =>
    [...rangeSold].filter(r => (parseFloat(r.profit) || 0) < 0)
      .sort((a, b) => parseFloat(a.profit) - parseFloat(b.profit)).slice(0, 5), [rangeSold]);

  // ── Category scoreboard (all time) ─────────────────────────
  const categories = useMemo(() => {
    if (!settings?.itemTypes || !rows) return [];
    return settings.itemTypes.map(type => {
      const match = r => getItemTypes(r).some(t => t.value === type.value);
      const all = rows.filter(r => match(r) && r.skin?.value);
      const sold = all.filter(r => parseFloat(r.sellPrice) > 0);
      const profit = sold.reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);
      const invested = all.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
      return { name: type.label, color: type.color || ZONE.purple, profit, invested, soldCount: sold.length, total: all.length };
    }).filter(c => c.invested > 0).sort((a, b) => b.profit - a.profit);
  }, [rows, settings]);

  const noData = !rows?.filter(r => r.skin?.value).length;
  if (noData) {
    return (
      <div className="zn-root">
        <ZoneHead pre="THE" hl="REPORT" sub="Your numbers, explained in plain English." />
        <div className="zn-empty">Nothing to analyse yet. Add and sell a few items first.</div>
      </div>
    );
  }

  const scopeLabel = range.days ? range.label.toUpperCase() : 'ALL TIME';
  const up = figures.holdPnl >= 0;

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="REPORT"
        sub={`The same numbers as Analytics, explained as you go. Pick a period below — it applies to everything stamped "${scopeLabel}".`}
      >
        <div className="zn-range zn-spacer">
          {RANGES.map(r => (
            <button key={r.key} type="button"
              className={`zn-chip ${rangeKey === r.key ? 'on' : ''}`}
              onClick={() => setRangeKey(r.key)}>
              {r.label}
            </button>
          ))}
        </div>
      </ZoneHead>

      {/* ── The story ──────────────────────────────── */}
      <motion.div className="zn-story"
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        Over all time you've put <b>${fmt(figures.totalInvested)}</b> into skins.
        {' '}{range.days ? `In the ${range.label.toLowerCase()}` : 'So far'}, you sold <b>{figures.soldCount}</b> item{figures.soldCount !== 1 ? 's' : ''} for{' '}
        <b>${fmt(figures.revenue)}</b>, which {figures.profit >= 0
          ? <>earned you <span className="good">{signed(figures.profit)}</span> in profit</>
          : <>lost you <span className="bad">{signed(figures.profit)}</span></>}.
        {' '}You still hold <b>{figures.unsoldCount}</b> item{figures.unsoldCount !== 1 ? 's' : ''} that cost <b>${fmt(figures.holdCost)}</b> and{' '}
        {figures.priced > 0
          ? <>are worth about <b>${fmt(figures.holdValue)}</b> today, so {up
              ? <>you'd pocket an extra <span className="good">{signed(figures.holdPnl)}</span> if you sold everything now</>
              : <>you'd take a <span className="bad">{signed(figures.holdPnl)}</span> hit if you sold everything now</>}</>
          : <>are waiting for live prices</>}.
      </motion.div>

      {/* ── 01 · Key numbers ───────────────────────── */}
      <Section num="01" title="The key numbers" scope={scopeLabel}
        explain="Each card is one question about your trading, answered. Hover the i to see exactly how it's calculated.">
        <div className="zn-kpis">
          <div className="zn-kpi">
            <div className="top"><span className="lbl">Money actually made</span>
              <Info text="Realized profit: for every item you sold in this period, sell price minus buy price minus fees, all added up. This is money in the bank, not paper value." /></div>
            <div className="val" style={{ color: pnlColor(figures.profit) }}>{signed(figures.profit)}</div>
            <div className="sub">from {figures.soldCount} completed sale{figures.soldCount !== 1 ? 's' : ''}</div>
          </div>
          <div className="zn-kpi">
            <div className="top"><span className="lbl">Return on the money used</span>
              <Info text="ROI: profit divided by what those sold items originally cost you. +20% means every $100 you spent came back as $120." /></div>
            <div className="val" style={{ color: figures.roi == null ? ZONE.mute : pnlColor(figures.roi) }}>
              {figures.roi == null ? 'n/a' : pct(figures.roi)}
            </div>
            <div className="sub">{figures.roi == null ? 'no sales in this period' : `on $${fmt(figures.soldCost)} spent`}</div>
          </div>
          <div className="zn-kpi">
            <div className="top"><span className="lbl">How often you win</span>
              <Info text="Win rate: the share of your sales that made any profit at all. Above 50% means you profit more often than not." /></div>
            <div className="val">{figures.winRate == null ? 'n/a' : `${figures.winRate}%`}</div>
            <div className="sub">{figures.winRate == null ? 'no sales in this period' : `${figures.wins} of ${figures.soldCount} sales made money`}</div>
          </div>
          <div className="zn-kpi">
            <div className="top"><span className="lbl">Typical sale</span>
              <Info text="Average profit per sale: total profit divided by number of sales. One big win can pull this up, so check the winners list below." /></div>
            <div className="val" style={{ color: figures.avgProfit == null ? ZONE.mute : pnlColor(figures.avgProfit) }}>
              {figures.avgProfit == null ? 'n/a' : signed(figures.avgProfit)}
            </div>
            <div className="sub">average per completed sale</div>
          </div>
          <div className="zn-kpi">
            <div className="top"><span className="lbl">Profit still on the table</span>
              <Info text="Unrealized P/L: what your current holdings are worth at today's Buff163 prices, minus what you paid. It becomes real profit only when you sell." /></div>
            <div className="val" style={{ color: pnlColor(figures.holdPnl) }}>{signed(figures.holdPnl)}</div>
            <div className="sub">across {figures.unsoldCount} items you still hold (right now)</div>
          </div>
        </div>
      </Section>

      {/* ── 02 · Profit journey ────────────────────── */}
      <Section num="02" title="Your profit journey" scope="ALL TIME"
        explain="Every sale you've ever made, in order. Each one nudges the line up (profit) or down (loss). Up and to the right is the goal; the dashed line is break-even.">
        <div className="zn-chart-panel">
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={cumulative} margin={{ top: 6, right: 18, left: -4, bottom: 0 }}>
              <defs>
                <linearGradient id="znCume" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ZONE.green} stopOpacity={0.75} />
                  <stop offset="100%" stopColor={ZONE.green} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false} tickFormatter={v => `$${v}`} width={56} />
              <Tooltip content={<ChartTip />} cursor={{ stroke: AXIS, strokeWidth: 2, strokeDasharray: '4 4' }} />
              <ReferenceLine y={0} stroke={AXIS} strokeWidth={2} strokeDasharray="6 4" />
              <Area type="monotone" dataKey="profit" stroke={AXIS} strokeWidth={3} fill="url(#znCume)" dot={false}
                activeDot={{ fill: ZONE.yellow, stroke: AXIS, strokeWidth: 3, r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="zn-reading">
            <b>How to read it:</b> the line ends at{' '}
            <b style={{ color: pnlColor(figures.lifetimeProfit) }}>{signed(figures.lifetimeProfit)}</b>, your total profit
            from every sale ever. Flat stretches are periods where you didn't sell anything.
          </div>
        </div>
      </Section>

      {/* ── 03 · Month by month ────────────────────── */}
      <Section num="03" title="Month by month" scope="ALL TIME"
        explain="Total profit from sales in each calendar month. Above the line the month made money; below it, the month lost money.">
        <div className="zn-chart-panel">
          <ResponsiveContainer width="100%" height={215}>
            <BarChart data={monthly} margin={{ top: 6, right: 18, left: -4, bottom: 0 }} barCategoryGap="26%">
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="label" tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false} />
              <YAxis tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false} tickFormatter={v => `$${v}`} width={56} />
              <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(19,19,19,0.06)' }} />
              <ReferenceLine y={0} stroke={AXIS} strokeWidth={3} />
              <Bar dataKey="profit" animationDuration={800} stroke={AXIS} strokeWidth={3}>
                {monthly.map((m, i) => (
                  <Cell key={i} fill={m.profit >= 0 ? ZONE.green : ZONE.hot} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {bestMonth && worstMonth && (
            <div className="zn-reading">
              <b>How to read it:</b> your best month was <b>{bestMonth.label}</b>{' '}
              (<b style={{ color: ZONE.win }}>{signed(bestMonth.profit)}</b> across {bestMonth.count} sales)
              {worstMonth.profit < 0 && <>; your toughest was <b>{worstMonth.label}</b>{' '}
              (<b style={{ color: ZONE.lose }}>{signed(worstMonth.profit)}</b>)</>}.
            </div>
          )}
        </div>
      </Section>

      {/* ── 04 · What you hold, over time ──────────── */}
      {holdingsHistory.length >= 2 && (
        <Section num="04" title="What your holdings are worth" scope="CURRENT ITEMS"
          explain="Two lines for the items you haven't sold: what you paid (the dashed line) and what they're worth at market price. The gap between them is your paper profit, or paper loss.">
          <div className="zn-chart-panel">
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={holdingsHistory} margin={{ top: 6, right: 18, left: -4, bottom: 0 }}>
                <defs>
                  <linearGradient id="znHold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ZONE.purple} stopOpacity={0.8} />
                    <stop offset="100%" stopColor={ZONE.purple} stopOpacity={0.06} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="date" tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false} tickFormatter={v => `$${v}`} width={56} />
                <Tooltip content={<PairTip />} cursor={{ stroke: AXIS, strokeWidth: 2, strokeDasharray: '4 4' }} />
                <Legend iconType="plainline" wrapperStyle={{ fontSize: 14.5, fontWeight: 800, fontFamily: 'Archivo, sans-serif', paddingTop: 8 }} />
                <Area type="monotone" name="What you paid" dataKey="cost" stroke={ZONE.orange} strokeWidth={3}
                  strokeDasharray="6 4" fill="none" dot={false} />
                <Area type="monotone" name="What it's worth" dataKey="value" stroke={AXIS} strokeWidth={3}
                  fill="url(#znHold)" dot={false} activeDot={{ fill: ZONE.yellow, stroke: AXIS, strokeWidth: 3, r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
            <div className="zn-reading">
              <b>How to read it:</b> right now the market line sits{' '}
              <b style={{ color: pnlColor(figures.holdPnl) }}>{up ? 'above' : 'below'}</b> the cost line by{' '}
              <b style={{ color: pnlColor(figures.holdPnl) }}>${fmt(figures.holdPnl)}</b>.{' '}
              {up ? "That's what you'd gain by selling everything today." : "That's how far your holdings are underwater today."}
            </div>
          </div>
        </Section>
      )}

      {/* ── 05 · Best & worst ──────────────────────── */}
      <Section num="05" title="Your best and worst sales" scope={scopeLabel}
        explain="The five sales that made you the most, and the five that cost you the most. If one item dominates a list, it's shaping most of your averages above.">
        <div className="zn-wl-cols">
          <div className="zn-wl-col">
            <div className="zn-wl-head" style={{ background: ZONE.lime }}>Made you the most</div>
            {winners.length === 0 && <div style={{ fontSize: 15, fontWeight: 700, color: ZONE.mute }}>No profitable sales in this period.</div>}
            {winners.map((r, i) => {
              const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
              return (
                <div className="zn-wl-row" key={i}>
                  <span className="zn-wl-rank">{i + 1}</span>
                  {img && <img src={img} alt="" />}
                  <span className="zn-wl-name" title={r.skin?.value}>{r.skin?.value}</span>
                  <span className="zn-wl-amt" style={{ color: ZONE.win }}>{signed(parseFloat(r.profit) || 0)}</span>
                </div>
              );
            })}
          </div>
          <div className="zn-wl-col">
            <div className="zn-wl-head" style={{ background: ZONE.pink }}>Cost you the most</div>
            {losers.length === 0 && <div style={{ fontSize: 15, fontWeight: 700, color: ZONE.mute }}>No losing sales in this period. Nice.</div>}
            {losers.map((r, i) => {
              const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
              return (
                <div className="zn-wl-row" key={i}>
                  <span className="zn-wl-rank">{i + 1}</span>
                  {img && <img src={img} alt="" />}
                  <span className="zn-wl-name" title={r.skin?.value}>{r.skin?.value}</span>
                  <span className="zn-wl-amt" style={{ color: ZONE.lose }}>{signed(parseFloat(r.profit) || 0)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ── 06 · Category scoreboard ───────────────── */}
      {categories.length > 0 && (
        <Section num="06" title="Which categories are working" scope="ALL TIME"
          explain="Everything you've bought, grouped by the categories you assigned. 'Earning' means its completed sales are net profitable; 'no sales yet' means the verdict is still out.">
          <div className="zn-cat-panel">
            {categories.map((c, i) => {
              const verdict = c.soldCount === 0
                ? { label: 'no sales yet', bg: 'rgba(19,19,19,0.06)' }
                : c.profit >= 0
                  ? { label: `earning · ${signed(c.profit)}`, bg: ZONE.lime }
                  : { label: `losing · ${signed(c.profit)}`, bg: ZONE.pink };
              return (
                <div className="zn-cat-row" key={i}>
                  <span className="zn-cat-dot" style={{ background: c.color }} />
                  <span className="zn-cat-name">{c.name}</span>
                  <span className="zn-cat-num">{c.total} item{c.total !== 1 ? 's' : ''} · {c.soldCount} sold</span>
                  <span className="zn-cat-num">${fmt(c.invested)} invested</span>
                  <span className="zn-verdict" style={{ background: verdict.bg }}>{verdict.label}</span>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <ZoneFooter stickers={[
        { text: 'prices via buff163 lowest asks', bg: ZONE.purple },
        { text: 'fees included in every profit figure', bg: ZONE.white },
        { text: `lifetime ${signed(figures.lifetimeProfit)}`, bg: figures.lifetimeProfit >= 0 ? ZONE.lime : ZONE.hot, right: true },
      ]} />
    </div>
  );
}
